import { useRef } from 'react';
import classes from './Landing.module.scss';
import ThemeBtn from '../../components/UI/ThemeBtn';
import HeroBannerRight from '../../assets/images/hero_banner_right.png';

import {
  AiOutlineProfile,
  AiOutlineFileText,
  AiOutlineSearch,
  AiOutlineSend,
  AiOutlineTeam,
  AiOutlineAppstore
} from "react-icons/ai";

import JobFinderShot from '../../assets/images/job-finder.png';
import CvStudioShot from '../../assets/images/cv-studio.png';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import StepContent from '@mui/material/StepContent';
import StepConnector from '@mui/material/StepConnector';

import { FLITZ_URL } from '../../constants/paths';

const CardItem = (props) => {
  const data = props.data;
  return (
    <div className={classes.lnCard}>
      <div style={{background: data.iconBg}}>
        {data.icon}
      </div>
      <h3>{data.title}</h3>
      <p>{data.desc}</p>
    </div>
  )
}

const Landing = () => {
  const cardSectionHead = useRef();

  const goToRegister = () => {
    window.location.href = FLITZ_URL;
  }

  const learnMoreCards = [
    {
      iconBg: 'rgba(38, 198, 218, 0.20)',
      icon: <AiOutlineProfile style={{color: '#10C2D8'}} />,
      title: "One master profile",
      desc: "Your full career history in a single profile — summary, experience, education, skills, languages, certifications and links. Every CV is generated from this one source of truth, so you never re-type anything."
    },
    {
      iconBg: 'rgba(252, 87, 59, 0.20)',
      icon: <AiOutlineFileText style={{color: '#FC573B'}} />,
      title: "AI-tailored CVs",
      desc: "Point CV Studio at a specific job ad and it generates a CV tailored to that posting — editable, exportable to PDF, and shareable via a public link."
    },
    {
      iconBg: 'rgba(190, 99, 249, 0.20)',
      icon: <AiOutlineSearch style={{color: '#B84FFD'}} />,
      title: "Job Finder",
      desc: "An AI deep-search scans job boards, aggregators and company career pages, then scores every posting against your profile — with the reasons it matched."
    },
    {
      iconBg: 'rgba(242, 201, 76, 0.20)',
      icon: <AiOutlineSend style={{color: '#F2C94C'}} />,
      title: "Applications",
      desc: "Turn any lead into a prepared application in one step. AI drafts it from your profile and the posting; send it by email and track its status."
    },
    {
      iconBg: 'rgba(39, 174, 96, 0.20)',
      icon: <AiOutlineTeam style={{color: '#27AE60'}} />,
      title: "References",
      desc: "Request references from past managers and colleagues with a personal note. They reply on a simple public page, and approved references can appear on your CV."
    },
    {
      iconBg: 'rgba(38, 198, 218, 0.20)',
      icon: <AiOutlineAppstore style={{color: '#26C6DA'}} />,
      title: "At a glance",
      desc: "Quick access to CV Studio, Job Finder, Applications and References, plus a dashboard widget with your tailored CVs, open job leads and applications in one card."
    }
  ];

  const getStarted = [
    {
      title: "Build your master profile",
      desc: "Import straight from LinkedIn, paste a CV or “Save to PDF” export, or type it in by hand. One profile holds your full career history — the single source everything else is built from."
    },
    {
      title: "Generate a tailored CV",
      desc: "Point CV Studio at a specific job ad and AI rewrites your CV to match it — reweighting your experience and wording. Edit it, export to PDF, or share it with a public link."
    },
    {
      title: "Deep-search for jobs",
      desc: "Job Finder scans job boards, aggregators and company career pages, then scores every posting against your profile and tells you why it matched. Searches run in the background and the page updates itself."
    },
    {
      title: "Prepare & send applications",
      desc: "Turn any lead into a prepared application in one step — AI drafts it from your profile and the posting. Send it by email and track its status from one place."
    },
    {
      title: "Collect references",
      desc: "Request references from past managers and colleagues with a personal note. They reply on a simple page, and approved references can be shown on your CV."
    }
  ];

  return (
    <>
      <div className={classes.heroBanner}>
        <div className={classes.left}>
          <div className={classes.leftContentWrapper}>
            <h1 className={classes.bannerHeading}>Automate your job search</h1>
            <p className={classes.bannerText}>CV Studio is the personal career module in Flitz — one master profile turned into job-tailored CVs, matching roles found for you, and your applications and references tracked, all in one place.</p>
            <div className={classes.btnWrapper}>
              <ThemeBtn
                text="Learn more"
                className={`me-2 ${classes.lmBtn}`}
                onClick={() => { cardSectionHead.current?.scrollIntoView({behavior: 'smooth'}); }}
              />
              <ThemeBtn text="Get Started" variant="primary" onClick={goToRegister} />
            </div>
          </div>
        </div>
        <div className={classes.right} style={{
          backgroundImage: `url(${HeroBannerRight})`,
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat'
        }}></div>
        <div className={classes.rightSpacing}></div>
      </div>
      <div className={classes.learnMore}>
        <h2 ref={cardSectionHead}>Everything for your job search, in one place</h2>
        <div className={classes.cardContainer}>
          {learnMoreCards.map((cardData, i) => {
            return <CardItem key={i} data={cardData} />
          })}
        </div>
      </div>
      <div className={classes.getStarted}>
        <div className={classes.lftSection}>
          <div className={classes.gsWrapper}>
            <h2>From profile to offer, step by step</h2>
            <Stepper orientation="vertical"
              connector={<StepConnector className="cstStepConnector" sx={{marginLeft: '18px'}} />}>
              {getStarted.map((step, index) => (
                <Step active={true} key={index}>
                  <StepLabel>
                    <h3 className={classes.stepLabel}>{step.title}</h3>
                  </StepLabel>
                  <StepContent sx={
                    index !== getStarted.length - 1 ?
                      { borderLeft: '2px solid var(--bs-primary)', marginLeft: '18px', paddingBottom: '15px' }
                      : { marginLeft: '18px', paddingBottom: '15px' }
                  }>
                    <p className={classes.stepDesc}>{step.desc}</p>
                  </StepContent>
                </Step>
              ))}
            </Stepper>
          </div>
        </div>
        <div className={classes.rtSection}>
          <div className={classes.mockStack}>
            <div className={`${classes.browserMock} ${classes.mockBack}`}>
              <div className={classes.browserBar}><span></span><span></span><span></span></div>
              <img src={CvStudioShot} alt="Flitz CV Studio — master profile and AI-tailored CVs" />
            </div>
            <div className={`${classes.browserMock} ${classes.mockFront}`}>
              <div className={classes.browserBar}><span></span><span></span><span></span></div>
              <img src={JobFinderShot} alt="Flitz Job Finder — AI deep-search scoring postings against your profile" />
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default Landing;
